/**
 * mediaUrl — THE single decision point for media display URLs.
 *
 * Callers state WHAT they render (a media reference + a variant); this
 * module decides HOW (passthrough, R2/Cloudflare transform, Supabase
 * public render URL, stable proxy URL, or per-request signing). Before
 * this existed, every loader chose between ~6 helpers and had to know
 * the rules — getting it wrong produced the 2026-06-12 production bugs
 * (expired tokens inside unstable_cache payloads; raw storage paths in
 * AvatarImage). See WIP_simplifying_media_architecture_2026_06_12.
 *
 * The two-function split IS the safety property:
 *  - `mediaUrl()` is synchronous and can only return stable, token-free
 *    URLs — safe inside unstable_cache / ISR / RSC payloads by
 *    construction. Private-tier refs resolve to null here.
 *  - `mediaUrlSigned*()` is the explicit, separately-named signing act
 *    for request-scoped private display. Never call it from a cached
 *    context.
 *
 * Variant vocabulary: `ImageVariants` (image-transform.service.ts) is
 * the ONLY registry. Do not add parallel size constants.
 */
import type { SupabaseClient } from '@supabase/supabase-js';

import { cdnImageUrl, isR2StableUrl } from './cdn-image-url.service';
import { ImageVariants, type ImageVariantKey } from './image-transform.service';
import { publicCoverUrl, type CoverVariant } from './public-media-url';
import { generatePublicThumbnailUrl } from './storage-path.service';
import { SignedUrlExpiry, StorageBuckets } from './storage-url.service';

export interface MediaRef {
  /** Storage bucket id. Plain string — several buckets (organization-media,
   * issue-comment-media, competition-media) predate the StorageBuckets enum. */
  bucket: string;
  /** Bucket-relative storage path. MAY hold a full URL (R2-migrated rows
   * store `https://cdn.ballee.co/...` in storage_path; Meteor legacy rows
   * can hold other full URLs). */
  path: string | null | undefined;
  /** External source (legacy S3, YouTube, Vimeo). Wins over path when set. */
  externalUrl?: string | null;
  /** R2 object key (e.g. `artist-media/<uuid>/photo/x.jpg`) once the
   * immutable original has been copied to R2. The AUTHORITATIVE residency
   * signal (D1): when set on a PUBLIC ref, the object is served as a bounded
   * Cloudflare transform from cdn.ballee.co — no Supabase render, no proxy,
   * no token. Wins over `path` (the legacy Supabase location). Null = not
   * yet migrated; fall back to the Supabase render / proxy path. */
  r2Key?: string | null;
  /** Display tier the CALLER knows: 'public' = this media is shown on a
   * publicly visible surface (public profile, open listing); 'private' =
   * RLS-gated content (documents, contracts, non-public media). */
  visibility: 'public' | 'private';
}

/** R2 public origin — matches the host cdnImageUrl detects as transformable
 * and the prefix legacy R2-migrated rows already store in storage_path. */
const R2_PUBLIC_ORIGIN = 'https://cdn.ballee.co';

/** Buckets whose objects are world-readable — Supabase public URLs are
 * stable and need no token or proxy. */
const PUBLIC_BUCKETS = new Set<string>([
  'organization-media',
  StorageBuckets.CHAT_ATTACHMENTS,
  'issue-comment-media',
  'competition-media',
]);

/** Private buckets with a public display tier served by the stable proxy
 * route (/api/media/cover). Today: dancer covers only. */
const PROXIED_PUBLIC_BUCKETS = new Set<string>([StorageBuckets.DANCER_MEDIA]);

function isFullUrl(value: string): boolean {
  return value.startsWith('http://') || value.startsWith('https://');
}

/** Map an ImageVariants key onto the cover-proxy's variant vocabulary. */
function coverVariantOf(variant: ImageVariantKey): CoverVariant {
  return variant === 'ORIGINAL' || variant === 'HERO' ? 'original' : 'gallery';
}

/** Supabase Storage image-transform shape (createSignedUrl / getPublicUrl). */
export interface SupabaseImageTransform {
  width?: number;
  height?: number;
  quality?: number;
  resize?: 'cover' | 'contain';
}

/**
 * Translate an `ImageVariants` key into the Supabase Storage transform shape.
 * THE single adapter from the canonical variant registry to a Supabase
 * `{ transform }` option — call sites name a variant, never inline widths.
 * Returns undefined for ORIGINAL (no transform → passthrough).
 */
export function supabaseTransformOf(
  variant: ImageVariantKey,
): SupabaseImageTransform | undefined {
  const opts = ImageVariants[variant];
  if (!opts) return undefined;
  return {
    width: opts.width,
    height: opts.height,
    quality: opts.quality,
    resize: opts.fit === 'contain' ? 'contain' : 'cover',
  };
}

/**
 * Stable, cache-safe display URL. NEVER signed — safe to embed in
 * unstable_cache payloads, RSC trees, and crawler-visible markup.
 *
 * Resolution order:
 *  1. externalUrl → as-is
 *  2. public + r2Key set → Cloudflare transform from the immutable R2 original
 *  3. no path and no externalUrl → null
 *  4. path is a full URL → R2 → Cloudflare transform; other hosts as-is
 *  5. public visibility + public bucket → Supabase public render URL
 *  6. public visibility + proxied private bucket → /api/media/cover URL
 *  7. anything else (private tier) → null — use mediaUrlSigned explicitly
 */
export function mediaUrl(
  ref: MediaRef,
  variant: ImageVariantKey,
): string | null {
  if (ref.externalUrl) return ref.externalUrl;

  // R2-resident public media: the bounded Cloudflare transform from the
  // immutable R2 original (D1/D4). Authoritative once an object is copied —
  // wins over the legacy Supabase `path`, and makes oversized originals
  // render (Cloudflare bounds the output; Supabase render rejected them).
  // Private refs NEVER take this path — cdn.ballee.co is unauthenticated.
  const r2Key = ref.r2Key?.trim();
  if (ref.visibility === 'public' && r2Key) {
    const r2Url = `${R2_PUBLIC_ORIGIN}/${r2Key.replace(/^\/+/, '')}`;
    return cdnImageUrl(r2Url, variant) ?? r2Url;
  }

  const path = ref.path?.trim();
  if (!path) return null;

  if (isFullUrl(path)) {
    return isR2StableUrl(path) ? (cdnImageUrl(path, variant) ?? path) : path;
  }

  if (ref.visibility === 'public') {
    if (PUBLIC_BUCKETS.has(ref.bucket)) {
      const base = supabasePublicUrl(ref.bucket, path);
      if (!base) return null;
      const opts = ImageVariants[variant];
      if (!opts) return base; // ORIGINAL
      return generatePublicThumbnailUrl(base, {
        width: opts.width,
        height: opts.height,
        quality: opts.quality,
        resize: opts.fit === 'contain' ? 'contain' : 'cover',
      });
    }
    if (PROXIED_PUBLIC_BUCKETS.has(ref.bucket)) {
      return publicCoverUrl(path, coverVariantOf(variant));
    }
    // Public display requested for a bucket with no public tier — a
    // modelling error at the call site, not a runtime condition.
    if (process.env.NODE_ENV !== 'production') {
      console.warn(
        `[mediaUrl] bucket "${ref.bucket}" has no public tier — returning null. Use mediaUrlSigned for private display.`,
      );
    }
    return null;
  }

  return null;
}

/**
 * Per-request signed URL for the PRIVATE display tier. Request scope
 * only (server actions, live RSC renders) — never inside unstable_cache.
 * Full-URL / external refs resolve without signing, mirroring mediaUrl.
 */
export async function mediaUrlSigned(
  client: SupabaseClient,
  ref: MediaRef,
  variant: ImageVariantKey,
): Promise<string | null> {
  const map = await mediaUrlSignedBatch(client, [ref], variant);
  const path = ref.path?.trim();
  return (path ? map.get(path) : null) ?? (ref.externalUrl || null);
}

/**
 * Batch variant of mediaUrlSigned, keyed by storage path. One
 * `createSignedUrls` round-trip when the variant carries no transform;
 * a parallel wave of single signs otherwise (Supabase's batch endpoint
 * cannot carry a transform — it is baked into the signed token).
 */
export async function mediaUrlSignedBatch(
  client: SupabaseClient,
  refs: MediaRef[],
  variant: ImageVariantKey,
): Promise<Map<string, string | null>> {
  const result = new Map<string, string | null>();

  // Pre-resolve non-signable refs (external / full URLs / empties).
  const toSign: { bucket: string; path: string }[] = [];
  for (const ref of refs) {
    const path = ref.path?.trim();
    if (!path) continue;
    if (ref.externalUrl) {
      result.set(path, ref.externalUrl);
    } else if (isFullUrl(path)) {
      result.set(
        path,
        isR2StableUrl(path) ? (cdnImageUrl(path, variant) ?? path) : path,
      );
    } else {
      toSign.push({ bucket: ref.bucket, path });
    }
  }
  if (toSign.length === 0) return result;

  const transform = supabaseTransformOf(variant);

  // Group by bucket — createSignedUrls is per-bucket.
  const byBucket = new Map<string, string[]>();
  for (const { bucket, path } of toSign) {
    const list = byBucket.get(bucket) ?? [];
    list.push(path);
    byBucket.set(bucket, list);
  }

  await Promise.all(
    [...byBucket.entries()].map(async ([bucket, paths]) => {
      if (transform) {
        const signed = await Promise.all(
          paths.map(async (p) => {
            const { data } = await client.storage
              .from(bucket)
              .createSignedUrl(p, SignedUrlExpiry.IMMEDIATE_DISPLAY, {
                transform,
              });
            return data?.signedUrl ?? null;
          }),
        );
        paths.forEach((p, i) => result.set(p, signed[i] ?? null));
      } else {
        const { data } = await client.storage
          .from(bucket)
          .createSignedUrls(paths, SignedUrlExpiry.IMMEDIATE_DISPLAY);
        paths.forEach((p, i) => result.set(p, data?.[i]?.signedUrl ?? null));
      }
    }),
  );

  return result;
}

/** Construct a Supabase public-object URL without a client round-trip —
 * getPublicUrl is pure string concatenation; doing it from env keeps
 * mediaUrl synchronous and callable anywhere. */
function supabasePublicUrl(bucket: string, path: string): string | null {
  const base = process.env.NEXT_PUBLIC_SUPABASE_URL;
  if (!base) return null;
  return `${base.replace(/\/+$/, '')}/storage/v1/object/public/${bucket}/${path}`;
}
